import React from 'react';
import { X, AlertTriangle, Copy, Check } from 'lucide-react';
import Button from './ui/Button';

// =============================================================================
// UploadResultsModal — what happened to a batch upload.
//
// The good rows are ALREADY SAVED by the time this opens (process_batch_upload
// commits when anything applied), so this is a partial-success result, not a
// failure: amber, applied-count first, and no "retry" affordance that would
// invite a re-upload of the whole sheet. Re-uploading is safe for shift
// requests (set_shift_preference upserts) but duplicates leave, so the fix
// path we steer to is "correct these rows", not "send it again".
//
// Rows are grouped BY CAUSE rather than listed flat, because a planner nearly
// always has one systemic problem — a column of shift codes their automation
// exported that don't match the configured ones, or names in another format.
// Thirty consecutive lines hide that; three headed groups make it obvious.
// =============================================================================

// Keyed on the backend's closed code set (services/excel/parser.py). Wording
// lives here so it can change without rebuilding the solver image. `hint` is a
// function so it can name the department's own shift codes.
const GROUPS = {
  shift_unknown: {
    title: 'Shift code not recognised',
    hint: (codes) => codes.length
      ? `Configured shift codes: ${codes.join(', ')}`
      : 'No shift types are configured for this department yet.',
  },
  shift_missing: {
    title: 'No shift code given',
    hint: () => 'A preference needs one row per shift, with the code in the Shift '
              + 'column. Only a dispreference may be left blank — that blocks the whole day.',
  },
  shift_inactive: {
    title: 'Shift type is not active',
    hint: () => 'Re-activate the shift type in Shift Types, or use an active one.',
  },
  unknown_employee: {
    title: 'Name not on the roster',
    hint: () => 'Names must match the roster. Check spelling, or add the person in Staff first.',
  },
  bad_dates: {
    title: 'Dates could not be used',
    hint: () => 'Use DD/MM/YYYY, and make sure the end date is on or after the start date.',
  },
  span_too_long: {
    title: 'Date range too long',
    hint: () => 'A single request covers at most one year — check for a mistyped year.',
  },
  oncall_conflict: {
    title: 'Already on leave',
    hint: () => 'An on-call request cannot sit on a day the person is on leave.',
  },
  row_error: {
    title: 'Row could not be read',
    hint: () => 'The row is malformed. Check for merged cells or stray characters.',
  },
  // severity "warning": the row IS applied. Never phrase these as something to
  // re-send, or a planner will re-upload rows that are already in.
  label_ignored: {
    title: 'Leave type ignored',
    hint: () => 'Only leave carries a type. These rows were applied without it.',
  },
  conflicting_rows: {
    title: 'Rows contradict each other',
    hint: () => 'Two rows said different things about the same day. The later row '
              + 'was used — check which one you meant.',
  },
  leave_overrides_call: {
    title: 'Leave cleared an on-call request',
    hint: () => 'Leave outranks a request, so the star was removed. Re-request a '
              + 'different day if needed.',
  },
  leave_vs_shift: {
    title: 'Leave overlaps a shift request',
    hint: () => 'Both were kept. The solver ranks leave first — remove the request '
              + 'yourself if it is no longer wanted.',
  },
};

const FALLBACK = { title: 'Not applied', hint: () => '' };

// A misaligned column can reject every row; render a bounded slice and leave
// the full list to Copy.
const MAX_PER_GROUP = 50;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(`${iso}T00:00:00`);
  return isNaN(d) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const UploadResultsModal = ({ isOpen, onClose, result, shiftCodes = [] }) => {
  const [copied, setCopied] = React.useState(false);
  React.useEffect(() => { if (isOpen) setCopied(false); }, [isOpen]);

  const rejections = React.useMemo(
    () => (result && Array.isArray(result.errors) ? result.errors : []),
    [result]
  );

  // Preserve spreadsheet order within a group — that is the order they'll be
  // fixed in — and order the groups by size, biggest cause first.
  const groups = React.useMemo(() => {
    const byCode = new Map();
    rejections.forEach((r) => {
      const code = (r && r.code) || 'row_error';
      if (!byCode.has(code)) byCode.set(code, []);
      byCode.get(code).push(r);
    });
    return [...byCode.entries()]
      .map(([code, rows]) => ({
        code,
        rows: [...rows].sort((a, b) => (a.row || 0) - (b.row || 0)),
        warning: (rows[0] && rows[0].severity) === 'warning',
        ...(GROUPS[code] || FALLBACK),
      }))
      // Rejections first (they need action), then warnings; biggest group first
      // within each.
      .sort((a, b) => (a.warning - b.warning) || (b.rows.length - a.rows.length));
  }, [rejections]);

  if (!isOpen || !result) return null;

  const applied = result.applied ?? 0;
  const rejected = result.rejected ?? rejections.length;
  const warnings = result.warnings ?? 0;

  const handleCopy = async () => {
    const text = rejections.map((r) => r.message).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      console.error('Clipboard write failed', e);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh]">

        <div className="p-8 pb-5">
          <div className="flex justify-between items-start mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 tracking-tight">Upload complete</h3>
                <p className="text-xs font-bold text-slate-400 mt-0.5">
                  {applied} {applied === 1 ? 'update' : 'updates'} applied
                  {rejected > 0 && <span className="text-amber-600">
                    {' · '}{rejected} {rejected === 1 ? 'row' : 'rows'} not applied
                  </span>}
                  {warnings > 0 && <span className="text-slate-400">
                    {' · '}{warnings} {warnings === 1 ? 'warning' : 'warnings'}
                  </span>}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-slate-500 leading-relaxed">
            The applied rows are saved. Correct any rejected rows in your
            spreadsheet and upload just those — there is no need to send the whole
            file again. Rows marked as warnings are already in; nothing to re-send.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-8 space-y-5">
          {groups.map(({ code, rows, title, hint, warning }) => {
            const shown = rows.slice(0, MAX_PER_GROUP);
            const hidden = rows.length - shown.length;
            return (
              <div key={code} className="bg-slate-50 rounded-xl border border-slate-100 p-4">
                <div className="flex items-baseline justify-between mb-2">
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">{title}</p>
                  <span className={`text-[10px] font-bold ${warning ? 'text-slate-400' : 'text-amber-600'}`}>
                    {rows.length}
                  </span>
                </div>
                <div className="space-y-1">
                  {shown.map((r, i) => (
                    <div key={`${r.row}-${i}`} className="flex items-baseline gap-2 text-xs">
                      <span className="font-mono text-[10px] text-slate-400 w-14 shrink-0">Row {r.row}</span>
                      <span className="font-bold text-slate-700 truncate">{r.name}</span>
                      {r.date && (
                        <span className="text-slate-400 shrink-0">
                          {formatDate(r.date)}{r.end_date ? `–${formatDate(r.end_date)}` : ''}
                        </span>
                      )}
                      {r.value && (
                        <span className={`font-mono text-[10px] px-1.5 py-0.5 rounded shrink-0 ${warning ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>
                          {r.value}
                        </span>
                      )}
                    </div>
                  ))}
                  {hidden > 0 && (
                    <p className="text-[10px] font-bold text-slate-400 pt-1">
                      and {hidden} more — use Copy for the full list
                    </p>
                  )}
                </div>
                {hint(shiftCodes) && (
                  <p className="text-[11px] text-slate-500 mt-2.5 pt-2.5 border-t border-slate-200/70">
                    {hint(shiftCodes)}
                  </p>
                )}
              </div>
            );
          })}
        </div>

        <div className="p-8 pt-5 flex gap-3">
          <Button
            variant="secondary"
            onClick={handleCopy}
            icon={copied ? Check : Copy}
            className="flex-1"
          >
            {copied ? 'Copied' : 'Copy list'}
          </Button>
          <Button variant="primary" onClick={onClose} className="flex-1">Done</Button>
        </div>
      </div>
    </div>
  );
};

export default UploadResultsModal;
